/**
 * Carbon content switcher (DESIGN.md "Navigation"): 48px segments on layer-02
 * (layer-accent in light), the selected one on the inverse fill. It filters a
 * view (the Command Center's pillar filter), so by default it is a group of
 * toggle buttons with aria-pressed, not tabs.
 *
 * `semantics="radio"` makes it a single-choice radio group instead
 * (role="radiogroup", role="radio" + aria-checked): one tab stop on the
 * checked segment, and the arrow keys move to and check the next one (the
 * WAI-ARIA radio group pattern). For a filter that is one value out of a set
 * (the Notifications category and severity filters).
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'

export interface ContentSwitcherItem {
  id: string
  label: ReactNode
  disabled?: boolean
}

export interface ContentSwitcherProps {
  items: ContentSwitcherItem[]
  /** The selected item's id. */
  value: string
  onChange: (id: string) => void
  'aria-label': string
  /** pressed (default): a group of aria-pressed toggle buttons. radio: a radiogroup with arrow-key roving focus. */
  semantics?: 'pressed' | 'radio'
  className?: string
}

const NEXT_KEYS: Record<string, 1 | -1> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

export function ContentSwitcher({ items, value, onChange, 'aria-label': ariaLabel, semantics = 'pressed', className }: ContentSwitcherProps) {
  const radio = semantics === 'radio'
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  // Radio mode: the checked segment is the one tab stop; with none checked, the first enabled one.
  const checkedIndex = items.findIndex((item) => item.id === value && !item.disabled)
  const tabStop = checkedIndex >= 0 ? checkedIndex : items.findIndex((item) => !item.disabled)

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, from: number) => {
    const step = NEXT_KEYS[e.key]
    if (!step) return
    e.preventDefault()
    for (let i = 1; i <= items.length; i++) {
      const next = (from + step * i + items.length) % items.length
      if (items[next].disabled) continue
      onChange(items[next].id)
      refs.current[next]?.focus()
      return
    }
  }

  return (
    <div
      role={radio ? 'radiogroup' : 'group'}
      aria-label={ariaLabel}
      className={clsx('flex flex-none overflow-x-auto [scrollbar-width:none]', className)}
    >
      {items.map((item, index) => {
        const pressed = item.id === value
        return (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[index] = el
            }}
            type="button"
            role={radio ? 'radio' : undefined}
            aria-checked={radio ? pressed : undefined}
            aria-pressed={radio ? undefined : pressed}
            tabIndex={radio ? (index === tabStop ? 0 : -1) : undefined}
            onKeyDown={radio ? (e) => onKeyDown(e, index) : undefined}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={clsx(
              'h-12 flex-none whitespace-nowrap border-r border-line-subtle px-4 text-body last:border-r-0',
              'transition-colors duration-110 ease-carbon disabled:cursor-not-allowed disabled:text-text-placeholder',
              pressed
                ? 'bg-inverse text-[color:var(--background)]'
                : 'bg-layer-2 text-text-secondary hover:bg-layer-hover hover:text-text-primary light:bg-layer-accent light:hover:bg-layer-hover',
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

export default ContentSwitcher
